import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  setDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/firebase';
import {
  FirestoreUserProfile,
  FirestoreCashbackTransaction,
  FirestoreWithdrawalRequest,
  FirestoreCashbackTransferRecord,
  FirestoreReferral,
  CountryCode,
} from '@/data/types';

function getStoredCountryCode(): CountryCode | undefined {
  try {
    const raw = localStorage.getItem('country');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed as CountryCode;
  } catch {
    // ignore
  }
  return undefined;
}

/** Derives a short human-readable referral code from the user's UID. */
function generateReferralCode(uid: string): string {
  return ('REF' + uid.slice(0, 5)).toUpperCase();
}

/**
 * Subscribes to the user's Firestore profile doc (users/{userId}).
 * Creates the doc with a fresh referral code and zeroed cashback summary
 * on first visit if it doesn't exist yet.
 */
export function useUserProfile(userId: string | null) {
  const [profile, setProfile] = useState<FirestoreUserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const docRef = doc(db, 'users', userId);

    const unsubscribe = onSnapshot(
      docRef,
      async (snap) => {
        if (snap.exists()) {
          const raw = snap.data() as Record<string, unknown>;
          const existingCode =
            typeof raw.referralCode === 'string' ? raw.referralCode.trim() : '';
          if (!existingCode) {
            try {
              const fn = httpsCallable(functions, 'ensureReferralCode');
              const res = await fn();
              const data = res.data as { referralCode?: string };
              const code =
                (typeof data?.referralCode === 'string' && data.referralCode) ||
                generateReferralCode(userId);
              setProfile({
                id: snap.id,
                ...raw,
                referralCode: code,
              } as FirestoreUserProfile);
            } catch {
              setProfile({
                id: snap.id,
                ...raw,
                referralCode: generateReferralCode(userId),
              } as FirestoreUserProfile);
            }
          } else {
            setProfile({ id: snap.id, ...raw } as FirestoreUserProfile);
          }
        } else {
          // Bootstrap the profile on first login
          const newProfile: Omit<FirestoreUserProfile, 'id'> = {
            referralCode: generateReferralCode(userId),
            cashbackSummary: {
              availableBalance: 0,
              pendingBalance: 0,
              totalEarned: 0,
            },
            countryCode: getStoredCountryCode(),
            createdAt: Timestamp.now(),
          };
          try {
            await setDoc(docRef, newProfile, { merge: true });
          } catch {
            // Gracefully fail if Firestore rules aren't configured yet
          }
          setProfile({ id: userId, ...newProfile });
        }
        setLoading(false);
      },
      () => {
        // Gracefully fail — leave profile null so UI falls back to empty state
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [userId]);

  return { profile, loading };
}

/**
 * Subscribes to users/{userId}/cashbackTransactions ordered by date desc.
 */
export function useCashbackTransactions(userId: string | null) {
  const [transactions, setTransactions] = useState<FirestoreCashbackTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setTransactions([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'users', userId, 'cashbackTransactions'),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as FirestoreCashbackTransaction[];
        setTransactions(items);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [userId]);

  return { transactions, loading };
}

/**
 * Subscribes to users/{userId}/withdrawalRequests ordered by date desc.
 */
export function useWithdrawalHistory(userId: string | null) {
  const [withdrawals, setWithdrawals] = useState<FirestoreWithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setWithdrawals([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'users', userId, 'withdrawalRequests'),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setWithdrawals(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as FirestoreWithdrawalRequest[]);
        setLoading(false);
      },
      () => setLoading(false),
    );

    return () => unsubscribe();
  }, [userId]);

  return { withdrawals, loading };
}

/**
 * Subscribes to users/{userId}/cashbackTransferRecords ordered by date desc.
 */
export function useTransferHistory(userId: string | null) {
  const [transfers, setTransfers] = useState<FirestoreCashbackTransferRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setTransfers([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'users', userId, 'cashbackTransferRecords'),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setTransfers(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as FirestoreCashbackTransferRecord[]);
        setLoading(false);
      },
      () => setLoading(false),
    );

    return () => unsubscribe();
  }, [userId]);

  return { transfers, loading };
}

/**
 * Subscribes to users/{userId}/referrals (people this user referred).
 */
export function useReferralNetwork(userId: string | null) {
  const [referrals, setReferrals] = useState<FirestoreReferral[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setReferrals([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'users', userId, 'referrals'),
      orderBy('registeredAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setReferrals(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })) as FirestoreReferral[],
        );
        setLoading(false);
      },
      () => setLoading(false),
    );

    return () => unsubscribe();
  }, [userId]);

  return { referrals, loading };
}

/**
 * Submits a referral code via Cloud Function (server writes referredBy* fields).
 */
export function useSubmitReferralCode() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (code: string) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'submitReferralCode');
      await fn({ code: code.trim() });
      return { ok: true as const };
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: string }).message === 'string'
          ? (e as { message: string }).message
          : 'تعذّر تطبيق الكود';
      setError(msg);
      return { ok: false as const, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}
